import {
  IconButton,
  useColorModeValue,
  Flex,
  Link,
  Text,
  Badge,
  Divider,
  Grid,
  GridItem,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { useContext } from 'react';
import InfoDrawer from '../../common/InfoDrawer';
import { CustomChevronLeftIcon } from '../../../theme/icons/CustomChevronLeftIcon';
import { CustomSendIcon } from '../../../theme/icons/CustomSendIcon';
import { MeContext } from '../../../context/MeContext';
import ChangeRole from './ChangeRole';
import DeleteAccount from './DeleteAccount';
import { ROLES } from '../../../constants/roles';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';

const InvitationMoreInfo = ({
  invitation,
  handleResendInvitation,
  getEntityName,
}) => {
  const isReadOnly = useIsReadOnly();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isRoleModalOpen,
    onOpen: onOpenRoleModal,
    onClose: onCloseRoleModal,
  } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onOpenDeleteModal,
    onClose: onCloseDeleteModal,
  } = useDisclosure();

  const meCtx = useContext(MeContext);
  const isOrgAdmin = meCtx.isOrgAdmin();
  const modeIconColor = useColorModeValue('secondaryDark08', '#DDDDDD');
  const modeBoxBg = useColorModeValue('gradient', 'secondaryDark');
  const modeTextBg = useColorModeValue('quaternaryBackground', 'primaryDark06');
  const modeBorderColor = useColorModeValue('primaryBackground', 'primaryDark');
  const modeLabelBg = useColorModeValue('white', 'primaryDark03');
  const bgColors = {
    modeBoxBg,
    modeTextBg,
    modeBorderColor,
    modeLabelBg,
  };

  const handleMoreInfoClick = (e) => {
    e.stopPropagation();
    onOpen();
  };

  return (
    <>
      <IconButton
        aria-label="More Info"
        onClick={handleMoreInfoClick}
        icon={
          <CustomChevronLeftIcon
            w="20px"
            h="20px"
            color={modeIconColor}
            transform="rotate(180deg)"
          />
        }
        variant="white"
        size="sm"
        w="36px"
        mr={1}
      />
      <InfoDrawer
        isOpen={isOpen}
        onClose={onClose}
        header={
          <Flex gap={2} alignItems="center" w="full">
            <Text textStyle="headingFamilyMedium">Invitation details</Text>
            {isOrgAdmin && (
              <>
                <Button variant="solid" size="m" onClick={onOpenRoleModal}>
                  <ChangeRole
                    isOpen={isRoleModalOpen}
                    onClose={onCloseRoleModal}
                    selectedAccount={invitation}
                  />
                </Button>
                <Button variant="link" size="m" onClick={onOpenDeleteModal}>
                  <DeleteAccount
                    isOpen={isDeleteModalOpen}
                    onClose={onCloseDeleteModal}
                    selectedAccount={invitation}
                  />
                </Button>
              </>
            )}
          </Flex>
        }
        footer={
          <Flex justifyContent="space-between" alignItems="center" w="full">
            <Link
              color={useColorModeValue('secondaryDark08', '#DDDDDD')}
              fontSize="14px"
              onClick={onClose}
              mr="30px"
            >
              <Text textStyle="headingFamilyMedium">Back to list</Text>
            </Link>
          </Flex>
        }
        bgColors={bgColors}
      >
        <Grid
          templateColumns={{
            base: '150px 1fr',
            md: '192px 1fr',
          }}
          gap={0}
        >
          <GridItem
            fontFamily="Inter Medium, sans-serif"
            fontSize="12px"
            color={modeIconColor}
            textTransform="uppercase"
            lineHeight="21px"
            padding="16px 4px 16px 22px"
          >
            Email
          </GridItem>
          <GridItem
            fontFamily="Inter Bold, sans-serif"
            fontSize="14px"
            padding="16px 10px 16px 22px"
            bg="quinaryBackground"
          >
            <Text wordBreak="break-word">{invitation.email}</Text>
          </GridItem>
          <GridItem colSpan={2} w="full">
            <Divider borderColor={modeBorderColor} />
          </GridItem>
          {!invitation.emailVerified && (
            <>
              <GridItem
                fontFamily="Inter Medium, sans-serif"
                fontSize="12px"
                color={modeIconColor}
                textTransform="uppercase"
                lineHeight="21px"
                padding="16px 4px 16px 22px"
              >
                Status
              </GridItem>
              <GridItem
                fontFamily="Inter Bold, sans-serif"
                fontSize="14px"
                padding="16px 10px 16px 22px"
                bg="quinaryBackground"
              >
                <Badge
                  alignContent="center"
                  align="center"
                  display="inline"
                  variant="pending"
                >
                  Pending
                </Badge>
                {isOrgAdmin && (
                  <Button
                    variant="link"
                    ml="10px"
                    size="sm"
                    isDisabled={isReadOnly}
                    onClick={() =>
                      handleResendInvitation(
                        invitation.email,
                        invitation.role,
                        invitation.entityId,
                        invitation.companyName
                      )
                    }
                  >
                    <Flex justifyContent="center" align="center">
                      <CustomSendIcon />
                      <Text textStyle="bodyFamilyMedium" pl="7px">
                        Resend
                      </Text>
                    </Flex>
                  </Button>
                )}
              </GridItem>
              <GridItem colSpan={2} w="full">
                <Divider borderColor={modeBorderColor} />
              </GridItem>
            </>
          )}
          <GridItem
            fontFamily="Inter Medium, sans-serif"
            fontSize="12px"
            color={modeIconColor}
            textTransform="uppercase"
            lineHeight="21px"
            padding="16px 4px 16px 22px"
          >
            Role
          </GridItem>
          <GridItem
            fontFamily="Inter Bold, sans-serif"
            fontSize="14px"
            padding="16px 10px 16px 22px"
            bg="quinaryBackground"
          >
            {ROLES[invitation.role].label}
            {invitation.role !== ROLES.org_admin.value &&
              invitation.entityId &&
              ` (${getEntityName(invitation.entityId)})`}{' '}
          </GridItem>
          <GridItem colSpan={2} w="full">
            <Divider borderColor={modeBorderColor} />
          </GridItem>
        </Grid>
      </InfoDrawer>
    </>
  );
};

export default InvitationMoreInfo;
